export { AppearanceControl, initializeAppearance } from "./theme.js";
import { ArrowRight, ArrowUpRight, CircleAlert, type LucideIcon } from "lucide-react";
import type { MouseEventHandler, ReactNode } from "react";
import { BrandMark } from "./BrandMark.js";
export { BrandMark } from "./BrandMark.js";
export { brandMarkFullSource } from "./brand-mark-assets.js";
export { CapabilityMap, type CapabilityMapAxis } from "./CapabilityMap.js";

/** Shared record header using the recruiting report identity and compact summary facts. */
export function RecordOverview({
  label,
  title,
  headingLevel = 2,
  summary,
  facts,
  actions,
}: {
  label: ReactNode;
  title: string;
  headingLevel?: 1 | 2;
  summary?: ReactNode;
  facts?: readonly { label: string; value: ReactNode }[] | undefined;
  actions?: ReactNode;
}) {
  const Heading = headingLevel === 1 ? "h1" : "h2";
  return (
    <section className="zy-record-overview">
      <p className="zy-record-overview__label">{label}</p>
      <Heading>{title}</Heading>
      {summary ? <p className="zy-record-overview__summary">{summary}</p> : null}
      {facts?.length ? (
        <dl>
          {facts.map((fact) => (
            <div key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value || "Not recorded"}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {actions ? <div className="zy-record-overview__actions">{actions}</div> : null}
    </section>
  );
}

function classes(...values: Array<string | false | undefined>): string {
  return values.filter(Boolean).join(" ");
}

export function Wordmark({
  href = "/",
  compact = false,
  sizes,
}: {
  href?: string;
  compact?: boolean;
  sizes?: string;
}) {
  return (
    <a className={classes("zy-wordmark", compact && "zy-wordmark--compact")} href={href}>
      <BrandMark sizes={sizes ?? (compact ? "32px" : "56px")} />
      <span>Zylarity</span>
    </a>
  );
}

export function ButtonLink({
  children,
  href,
  variant = "primary",
  external = false,
  onClick,
}: {
  children: ReactNode;
  href: string;
  variant?: "primary" | "secondary" | "quiet";
  external?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}) {
  return (
    <a
      className={`zy-button zy-button--${variant}`}
      href={href}
      {...(onClick ? { onClick } : {})}
      {...(external ? { rel: "noreferrer", target: "_blank" } : {})}
    >
      <span>{children}</span>
      {external ? (
        <ArrowUpRight aria-hidden="true" size={16} strokeWidth={1.8} />
      ) : (
        <ArrowRight aria-hidden="true" size={16} strokeWidth={1.8} />
      )}
    </a>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="zy-eyebrow">{children}</p>;
}

export function StatusBadge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "positive" | "attention" | "neutral" | "information";
}) {
  return <span className={`zy-status zy-status--${tone}`}>{children}</span>;
}

export function SectionHeading({
  eyebrow,
  title,
  copy,
  align = "left",
}: {
  eyebrow?: string;
  title: string;
  copy?: string;
  align?: "left" | "center";
}) {
  return (
    <header className={`zy-section-heading zy-section-heading--${align}`}>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <h2>{title}</h2>
      {copy ? <p>{copy}</p> : null}
    </header>
  );
}

export function PageHeader({
  eyebrow,
  title,
  copy,
  actions,
}: {
  eyebrow: string;
  title: string;
  copy?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="zy-page-header">
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1>{title}</h1>
        {copy ? <p>{copy}</p> : null}
      </div>
      {actions ? <div className="zy-page-header__actions">{actions}</div> : null}
    </header>
  );
}

export function MetricCard({
  label,
  value,
  detail,
  icon: Icon,
}: {
  label: string;
  value: string;
  detail: string;
  icon?: LucideIcon;
}) {
  return (
    <article className="zy-metric-card">
      <div className="zy-metric-card__top">
        <span>{label}</span>
        {Icon ? <Icon aria-hidden="true" size={18} strokeWidth={1.7} /> : null}
      </div>
      <strong>{value}</strong>
      <p>{detail}</p>
    </article>
  );
}

export function DataList({ items }: { items: ReadonlyArray<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="zy-data-list">
      {items.map((item) => (
        <div key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Timeline({
  items,
}: {
  items: ReadonlyArray<{
    label: string;
    detail: string;
    time: string;
    tone?: "complete" | "current" | "future";
  }>;
}) {
  return (
    <ol className="zy-timeline">
      {items.map((item) => (
        <li
          className={`zy-timeline__item zy-timeline__item--${item.tone ?? "future"}`}
          key={item.label}
        >
          <span aria-hidden="true" className="zy-timeline__marker" />
          <div>
            <div className="zy-timeline__title">
              <strong>{item.label}</strong>
              <time>{item.time}</time>
            </div>
            <p>{item.detail}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function Notice({
  children,
  title,
  tone = "information",
}: {
  children: ReactNode;
  title: string;
  tone?: "information" | "attention";
}) {
  return (
    <aside className={`zy-notice zy-notice--${tone}`}>
      <CircleAlert aria-hidden="true" size={19} strokeWidth={1.8} />
      <div>
        <strong>{title}</strong>
        <p>{children}</p>
      </div>
    </aside>
  );
}

export function EmptyState({
  title,
  copy,
  action,
}: {
  title: string;
  copy: string;
  action?: ReactNode;
}) {
  return (
    <div className="zy-empty-state">
      <span aria-hidden="true" className="zy-empty-state__geometry" />
      <h3>{title}</h3>
      <p>{copy}</p>
      {action}
    </div>
  );
}

export const UI_FOUNDATION = Object.freeze({
  breakpoints: ["390px", "768px", "1024px", "1440px"],
  motionPolicy: "reduced-motion-safe",
  contrastTarget: "WCAG 2.2 AA",
});
export {
  PublicJobCard,
  PublicJobDetail,
  publicJobCompensation,
  publicJobTravel,
  publicJobDate,
} from "./PublicJobPresentation.js";
